import { __awaiter } from "tslib";
import Time from 'date-and-time';
import { Container, Service } from './container';
import { Config } from './config';
import jwtDecode from 'jwt-decode';
import { AppState } from '../etc/stores';
import { Checkout } from './checkout';
import { AUTH_STORAGE_FLAGS, SessionManager } from './session-manager';
import { Backend } from './backend';
import sessionKeys from '../etc/sessionKeys';
const BIGCOMMERCE_CONNECTING_ACCOUNT = 'bigcommerce_connecting_account';
export class Bigcommerce extends Service {
    constructor() {
        super(...arguments);
        this.config = this.ctx.get(Config);
        this.checkout = this.ctx.get(Checkout);
        this.backend = this.ctx.get(Backend);
        this.session = this.ctx.get(SessionManager);
    }
    getCurrentMember() {
        return __awaiter(this, void 0, void 0, function* () {
            const customer = yield fetch(`https://${window['location'].host}/customer/current.jwt?app_client_id=${process.env.BIGCOMMERCE_APP_ID}`, {
                credentials: 'include'
            });
            const response = yield (yield customer.blob()).text();
            return !response.includes('error') && response;
        });
    }
    getAuthState() {
        var _a;
        return __awaiter(this, void 0, void 0, function* () {
            const { account } = yield this.config.getSiteSettings();
            const token = yield this.getCurrentMember();
            const decodedCustomer = token && jwtDecode(token);
            return {
                token,
                userId: (_a = decodedCustomer === null || decodedCustomer === void 0 ? void 0 : decodedCustomer.customer) === null || _a === void 0 ? void 0 : _a.id.toString(),
                provider: 'bigcommerce',
                account
            };
        });
    }
    getOrderData(orderId) {
        return __awaiter(this, void 0, void 0, function* () {
            try {
                const response = yield fetch(`https://${window['location'].host}/api/storefront/order/${orderId}`, {
                    credentials: 'include'
                });
                const order = JSON.parse(yield (yield response.blob()).text());
                return order;
            }
            catch (error) {
                return null;
            }
        });
    }
    isCurrentCartPage() {
        return window.location.href.includes('/cart.php');
    }
    isCurrentRemindCodePage() {
        return this.isCurrentCartPage();
    }
    getCouponsDiscountAndCodes(coupons) {
        return coupons.reduce((acc, coupon) => ({
            discountedAmount: acc.discountedAmount + coupon.discountedAmount,
            couponsCode: [...acc.couponsCode, coupon.code]
        }), { discountedAmount: 0, couponsCode: [] });
    }
    registerWebsiteEvents() {
        var _a, _b, _c, _d, _e;
        return __awaiter(this, void 0, void 0, function* () {
            const currentPathName = window['location'].pathname;
            const checkoutRegx = /^.*?\bcheckout\b.*?\border-confirmation\b.*?$/;
            if (this.isCurrentRemindCodePage()) {
                const checkoutValue = Number((document.querySelector('.cart-total-grandTotal > span') || {
                    textContent: '0'
                }).textContent.replace(/[^0-9.-]+/g, ''));
                this.session.set(sessionKeys.CHECKOUT_VALUE, checkoutValue, {
                    expires: Time.addDays(new Date(), 1)
                });
            }
            if (currentPathName.match(checkoutRegx)) {
                const { orderId, account } = this.config.getSiteSettings();
                if (orderId) {
                    const order = yield this.getOrderData(orderId);
                    const { discountedAmount, couponsCode } = this.getCouponsDiscountAndCodes(order.coupons);
                    if (order) {
                        const authInit = yield this.getAuthState();
                        yield this.backend.authenticate(authInit);
                        this.checkout.handleCheckout({
                            transactionId: orderId,
                            externalTransactionId: orderId.toString(),
                            account,
                            currency: (_a = order.currency) === null || _a === void 0 ? void 0 : _a.code,
                            externalCustomerId: order.customerId === 0 ? null : order.customerId.toString(),
                            value: Math.trunc(order.baseAmount * 100 - discountedAmount * 100),
                            externalCustomerEmail: (_b = order.billingAddress) === null || _b === void 0 ? void 0 : _b.email,
                            externalCustomerPhone: (_c = order.billingAddress) === null || _c === void 0 ? void 0 : _c.phone,
                            externalCustomerFirstName: (_d = order.billingAddress) === null || _d === void 0 ? void 0 : _d.firstName,
                            externalCustomerLastName: (_e = order.billingAddress) === null || _e === void 0 ? void 0 : _e.lastName,
                            couponsCode
                        });
                    }
                }
            }
            if ((yield this.getCurrentMember()) &&
                this.session.get(BIGCOMMERCE_CONNECTING_ACCOUNT)) {
                this.session.remove(BIGCOMMERCE_CONNECTING_ACCOUNT);
                const getData = () => __awaiter(this, void 0, void 0, function* () {
                    if (this.session.get(AUTH_STORAGE_FLAGS.referral)) {
                        const searchParams = new URLSearchParams(this.session.get(AUTH_STORAGE_FLAGS.referral));
                        const campaign = searchParams.get('twism-campaign') || searchParams.get('campaign');
                        const linkId = searchParams.get('twism-linkId') || searchParams.get('LinkId');
                        if (campaign === 'business-referral' && linkId) {
                            const referralReferredState = yield this.backend.getReferralByLinkId(linkId);
                            AppState.update(state => (Object.assign(Object.assign({}, state), { referralReferredState })));
                        }
                        return {
                            category: 'referredCoin',
                            authFlow: 'referred-redeem'
                        };
                    }
                    if (this.session.get(AUTH_STORAGE_FLAGS.customerImport)) {
                        return {
                            authFlow: 'customer-import',
                            popoverOpen: true,
                            popoverRouteOnOpen: {
                                route: '/customer-import-welcome'
                            }
                        };
                    }
                    if (this.session.get(AUTH_STORAGE_FLAGS.customerAcquisition)) {
                        return {
                            authFlow: 'customer-acquisition',
                            popoverOpen: true,
                            popoverRouteOnOpen: {
                                route: '/customer-acquisition'
                            }
                        };
                    }
                    if (this.session.get(AUTH_STORAGE_FLAGS.welcomeCoins)) {
                        return {
                            category: 'welcomeCoinAvailable',
                            authFlow: 'free-coins'
                        };
                    }
                    if (this.session.get(AUTH_STORAGE_FLAGS.fastRedemption)) {
                        return {
                            category: 'fastRedemption',
                            authFlow: 'fast-redemption'
                        };
                    }
                    return {
                        popoverOpen: true
                    };
                });
                const flow = yield getData();
                AppState.update(state => (Object.assign(Object.assign({}, state), { website: Object.assign(Object.assign(Object.assign({}, state.website), flow), { connectingAccount: true }) })));
            }
        });
    }
    getCheckoutInformation() {
        return __awaiter(this, void 0, void 0, function* () {
            const { orderId } = this.config.getSiteSettings();
            if (!orderId) {
                return null;
            }
            return {
                orderId,
                email: 'example@site.com' // FIXME
            };
        });
    }
    isConnectingAccount() {
        return this.session.get(BIGCOMMERCE_CONNECTING_ACCOUNT);
    }
    openLoginModal(data) {
        this.session.set(BIGCOMMERCE_CONNECTING_ACCOUNT, true);
        window.location.href = '/login.php';
        if (data && data.mode == 'login') {
            window.location.href = '/login.php';
        }
        else {
            window.location.href = '/login.php?action=create_account';
        }
    }
}
Container.add(Bigcommerce);
